import { CancerConfig } from '../configs/cancerConfig';
import { yupResolver } from '@hookform/resolvers/yup';
import Modal from '@oncolens/oncolens-ui/lib/components/Common/Modal';
import Button from '@oncolens/oncolens-ui/lib/components/FormControls/Button';
import ButtonWithLoading from '@oncolens/oncolens-ui/lib/components/FormControls/ButtonWithLoading';
import DatePicker from '@oncolens/oncolens-ui/lib/components/FormControls/DatePicker';
import Listbox from '@oncolens/oncolens-ui/lib/components/FormControls/Listbox';
import TextInput from '@oncolens/oncolens-ui/lib/components/FormControls/TextInput';
import Typeahead from '@oncolens/oncolens-ui/lib/components/FormControls/Typeahead/Typeahead';
import {
  BiomarkerPanelType,
  PanelDropdownItemDto,
  PanelDto
} from '@oncolens/oncolens-ui/lib/components/BiomarkerResults/BiomarkerResults.api';
import clsx from 'clsx';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FaPencilAlt, FaTrash } from 'react-icons/fa';
import * as Disease from '../domain/Disease';
import { BiomarkerPanelForCreationDto, BiomarkerPanelForUpdateDto } from '../dtos/Biomarker';
import {
  useCreateBiomarkerPanelsMocked,
  useDeleteBiomarkerPanelMocked,
  useUpdateBiomarkerPanelMocked
} from '../hooks/useBiomarkers';
import { usePostCaseConfigs } from '../hooks/PostCaseUtilities';
import { biomarkerPanelSchema } from '../Biomarkers/schema';
import BiomarkerList from './BiomarkerList';
import { useBiomarkerContext } from './PatientBiomarkerForm';

interface BiomarkerPanelProps {
	data: PanelDto;
}

/// Represents the top level panel object for a set of biomarkers.
///
/// This component does a lot of things currently, so here is how each piece fits in:
///
/// - The panel itself has two states - editing and not editing. Editing shows the form fields. Editing
///   occurs either when adding a new panel, or modifying an existing one.
/// - When not editing, the panel shows its data - name, type, etc.
/// - Updates happen 'locally' before the form is saved - meaning that we have
///   multiple methods for updating, creating, and removing/deleting,
///   some of which make API calls to persist the data (when we are in autosave status),
///   or just modify the local data. API calls also modify the current state to keep the form up to date
///   after saving.

const BiomarkerPanel = ({ data }: BiomarkerPanelProps) => {
  const context = useBiomarkerContext();
  const { id, biomarkers } = data;
  const isEditing = context.activeIds.isEditingPanel;
  const [isApiActionExecuting, setApiActionExecuting] = useState(false);
  const createPanelMutation = useCreateBiomarkerPanelsMocked();
  const updatePanelMutation = useUpdateBiomarkerPanelMocked();
  const deletePanelMutation = useDeleteBiomarkerPanelMocked();
  const previouslySaved = Boolean(data.panelName);

  const resetSelection = (firstPanel?: PanelDropdownItemDto) => {
    context.setActiveIds((previous) => ({
      ...previous,
      selectedPanelId: firstPanel ? firstPanel.panel?.id || firstPanel.panelOrder?.id || null : null
    }));
  };

  const handleApiCreate = (formValues: BiomarkerPanelForCreationDto) => {
    setApiActionExecuting(true);

    createPanelMutation
      .mutateAsync({
        patientId: context.formIds.patientId || '',
        data: [formValues]
      })
      .then((newData) => {
        setApiActionExecuting(false);
        setInactive();
        context.setBiomarkerPanels((panelItems: any) => {
          const updatedValue = panelItems.map((item: any) =>
            item.panel?.id === id
              ? {
                ...item,
                panel: {
                  ...newData[0],
                  biomarkers
                }
							  }
              : item
          );

          context.persistToForm(updatedValue);
          context.setActiveIds((previous) => ({ ...previous, selectedPanelId: newData[0].id }));
          return updatedValue;
        });
      })
      .catch(() => {
        setApiActionExecuting(false);
      });
  };

  const handleApiUpdate = (formValues: BiomarkerPanelForUpdateDto) => {
    setApiActionExecuting(true);

    updatePanelMutation
      .mutateAsync({
        panelId: id,
        data: formValues
      })
      .then(() => {
        context.setBiomarkerPanels((panelItems) => {
          const updatedValue = panelItems.map((item) =>
            item.panel?.id === id
              ? ({
                ...item,
                panel: {
                  id,
                  ...formValues,
                  biomarkers
                }
							  } as PanelDropdownItemDto)
              : item
          );

          context.persistToForm(updatedValue);

          return updatedValue;
        });

        setApiActionExecuting(false);
        setInactive();
      })
      .catch(() => {
        setApiActionExecuting(false);
      });
  };

  const handleApiDelete = () => {
    deletePanelMutation
      .mutateAsync(id)
      .then(() => {
        setApiActionExecuting(false);
        setInactive();
        context.setBiomarkerPanels((panelItems) => {
          const updatedValue = panelItems.filter((item) => item.panel?.id !== id);

          context.persistToForm(updatedValue);
          resetSelection(updatedValue.length ? updatedValue[0] : undefined);
          return updatedValue;
        });
      })
      .catch(() => {
        setApiActionExecuting(false);
      });
  };

  const handleUpdate = (formValues: any) => {
    context.setBiomarkerPanels((panelItems) => {
      const updatedValue = panelItems.map((item) =>
        item.panel?.id === id ? { ...item, panel: { ...formValues } } : item
      );

      context.persistToForm(updatedValue);

      return updatedValue;
    });

    setInactive();
  };

  // Cancel occurs instead of remove when we already 'saved' the item
  // and are now updating it (vs the form showing up when adding a new panel)
  const handleCancel = () => {
    if (!previouslySaved) {
      handleRemove();
    } else {
      setInactive();
    }
  };

  const handleRemove = () => {
    context.setBiomarkerPanels((panelItems) => {
      const updatedValue = panelItems.filter(
        (item) => item.type !== BiomarkerPanelType.Panel || item.panel?.id !== id
      );

      context.persistToForm(updatedValue);
      resetSelection(updatedValue.length ? updatedValue[0] : undefined);

      return updatedValue;
    });
    setInactive();
  };

  const setActive = () => {
    context.setActiveIds((previous) => ({ ...previous, isEditingPanel: true }));
  };

  const setInactive = () => {
    context.setActiveIds((previous) => ({ ...previous, isEditingPanel: false }));
  };

  return (
    <div
      data-cy="biomarker-panel"
      className="relative mb-3 transition-colors duration-200 ease-in-out bg-white border border-gray-300 rounded-md shadow-md"
    >
      {isEditing ? (
        <div className="grid grid-cols-3 gap-3 p-3">
          <BiomarkerPanelForm
            data={data}
            isInAutosave={context.isInAutosave}
            previouslySaved={previouslySaved}
            isApiActionExecuting={isApiActionExecuting}
            onCancel={handleCancel}
            onUpdate={handleUpdate}
            onApiCreate={handleApiCreate}
            onApiUpdate={handleApiUpdate}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 p-3 pb-4">
          <BiomarkerPanelValues
            data={data}
            isInAutosave={context.isInAutosave}
            onDelete={handleApiDelete}
            onRemove={handleRemove}
            onSetActive={setActive}
          />
          <BiomarkerList panelId={id} biomarkers={biomarkers} />
        </div>
      )}
      {
        <div
          className={clsx(
            'absolute top-0 left-0 w-full h-full bg-gray-300 opacity-0 pointer-events-none transition-opacity duration-300',
            ((Boolean(context.activeIds.isEditingPanel) && !isEditing) ||
							(Boolean(context.activeIds.biomarkerId) &&
								!biomarkers.some((x) => x.id === context.activeIds.biomarkerId))) &&
							'opacity-50 cursor-not-allowed pointer-events-auto'
          )}
        ></div>
      }
    </div>
  );
};

interface BiomarkerPanelFormProps {
	data: PanelDto;
	previouslySaved: boolean;
	isInAutosave: boolean;
	isApiActionExecuting: boolean;
	onCancel: () => void;
	onUpdate: (formValues: any) => void;
	onApiCreate: (formValues: BiomarkerPanelForCreationDto) => void;
	onApiUpdate: (formValues: BiomarkerPanelForUpdateDto) => void;
}

const BiomarkerPanelForm = ({
  data,
  previouslySaved,
  isInAutosave,
  isApiActionExecuting,
  onCancel,
  onUpdate,
  onApiCreate,
  onApiUpdate
}: BiomarkerPanelFormProps) => {
  const {
    getValues,
    trigger,
    control,
    reset,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    resolver: yupResolver(biomarkerPanelSchema)
  });

  const locationValue = watch('cancerLocationId');
  const diseaseTypeValue = watch('cancerPathologyId');
  useEffect(() => {
    reset(data);
  }, []);

  const configs = usePostCaseConfigs(['cancer']);

  const [pathologyOtherIds, locations] = React.useMemo(() => {
    const locations = Disease.mapCancerConfig(configs?.cancer);
    return [Disease.getCancerConfigPathologyOtherUuids(configs?.cancer), locations];
  }, [configs?.cancer]);

  const { pathologies } = Disease.getSubCollectionsFromCancerType(configs?.cancer, locationValue);

  const validateCreateUpdate = (createUpdateAction: () => void) => {
    trigger()
      .then((isValid) => {
        if (isValid) {
          createUpdateAction();
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const handleCancel = () => {
    onCancel();
    reset(data);
  };

  const handleUpdate = () => {
    validateCreateUpdate(() => {
      onUpdate(getValues());
    });
  };

  const handleApiUpdate = () => {
    validateCreateUpdate(() => {
      onApiUpdate(getValues() as BiomarkerPanelForUpdateDto);
    });
  };

  const handleApiCreate = () => {
    validateCreateUpdate(() => {
      onApiCreate(getValues() as BiomarkerPanelForCreationDto);
    });
  };

  return (
    <>
      <div className="col-span-3">
        <h2 className="pb-0.5 pr-1 inline-block text-base font-semibold border-b-2 border-oncolensBlue-400">
          {previouslySaved ? 'Update Panel' : 'Add New Panel'}
        </h2>
      </div>

      <Controller
        name="cancerLocationId"
        control={control}
        render={({ field }) => {
          const selectedItem = locations.find((location) => location.value === field.value) || null;
          return (
            <Typeahead
              id="panel-cancer-location-id"
              label="Location (or Topography)"
              required
              data={locations}
              className="col-span-3 sm:col-span-2"
              error={errors?.cancerLocationId?.message}
              selected={selectedItem}
              placeholder="Start typing a location..."
              options={{ clearable: true }}
              sortingOptions={{ sortable: true, sortProperty: 'label' }}
              onChange={(item) => {
                const newCancerType = Disease.getCancerTypeByLocation(item?.value, configs);
                field.onChange(item ? item.value : '');
                setValue('cancerTypeId', newCancerType?.value);
                setValue('cancerPathologyId', '');
              }}
            />
          );
        }}
      />

      <Controller
        name="cancerPathologyId"
        control={control}
        render={({ field }) => (
          <Listbox
            label="Disease Type"
            required={true}
            data={pathologies}
            error={errors?.cancerPathologyId?.message}
            selected={field.value}
            setSelected={(selected) => {
              field.onChange(selected);
            }}
            wrapperProps={{
              className: clsx('col-span-3 sm:col-span-1')
            }}
          />
        )}
      />

      {Boolean(pathologyOtherIds) && pathologyOtherIds.some((x) => x === diseaseTypeValue) && (
        <Controller
          name="cancerPathologyOtherComments"
          control={control}
          render={({ field }) => (
            <TextInput
              type="text"
              id="cancerPathologyOtherComments"
              htmlName="cancerPathologyOtherComments"
              value={field.value}
              onChange={field.onChange}
              label="Comments"
              wrapperProps={{
                className: 'col-span-3 sm:col-span-1 sm:col-start-3'
              }}
            />
          )}
        />
      )}

      <Controller
        name="panelName"
        control={control}
        render={({ field }) => (
          <TextInput
            id="panelName"
            label="Panel Name"
            type="text"
            htmlName="panelName"
            required={true}
            error={errors?.panelName?.message}
            value={field.value}
            onChange={field.onChange}
            wrapperProps={{ className: 'col-span-3 sm:col-span-2' }}
          />
        )}
      />

      <Controller
        name="reportDate"
        control={control}
        render={({ field }) => (
          <DatePicker
            id="reportDate"
            label="Report Date"
            htmlName="reportDate"
            selected={field.value}
            setSelected={field.onChange}
            required
            error={errors?.reportDate?.message}
            wrapperProps={{ className: 'col-span-3 sm:col-span-1' }}
          />
        )}
      />

      <Controller
        name="panelType"
        control={control}
        render={({ field }) => (
          <TextInput
            id="panelType"
            label="Panel Type"
            type="text"
            htmlName="panelType"
            value={field.value}
            onChange={field.onChange}
            wrapperProps={{ className: 'col-span-3 sm:col-span-2' }}
          />
        )}
      />

      <Controller
        name="collectionDate"
        control={control}
        render={({ field }) => (
          <DatePicker
            id="collectionDate"
            label="Collection Date"
            htmlName="collectionDate"
            required
            selected={field.value}
            setSelected={field.onChange}
            error={errors?.collectionDate?.message}
            wrapperProps={{ className: 'col-span-3 sm:col-span-1' }}
          />
        )}
      />

      <Controller
        name="reportingLab"
        control={control}
        render={({ field }) => (
          <TextInput
            id="reportingLab"
            label="Reporting Lab"
            type="text"
            htmlName="reportingLab"
            value={field.value}
            onChange={field.onChange}
            wrapperProps={{ className: 'col-span-3 sm:col-span-2' }}
          />
        )}
      />

      <Controller
        name="collectionSite"
        control={control}
        render={({ field }) => (
          <TextInput
            id="collectionSite"
            label="Collection Site"
            type="text"
            htmlName="collectionSite"
            value={field.value}
            onChange={field.onChange}
            wrapperProps={{ className: 'col-span-3 sm:col-span-1' }}
          />
        )}
      />
      <div className="flex my-1 col-span-full">
        <div className="flex-1 block mr-2 sm:inline-block sm:ml-auto sm:flex-initial">
          <Button
            buttonStyle="secondary"
            buttonColor="blue"
            onClick={handleCancel}
            type="button"
            className="block rounded sm:inline-block sm:py-1"
          >
						Cancel
          </Button>
        </div>
        <div className="flex-1 block sm:inline-block sm:flex-initial">
          {isInAutosave ? (
            <ButtonWithLoading
              buttonStyle="primary"
              onClick={previouslySaved ? handleApiUpdate : handleApiCreate}
              loading={isApiActionExecuting}
              buttonColor="blue"
              className="inline-block rounded sm:px-4 sm:py-1"
            >
              {previouslySaved ? 'Update' : 'Add'}
            </ButtonWithLoading>
          ) : (
            <Button
              buttonStyle="primary"
              buttonColor="blue"
              onClick={handleUpdate}
              className="inline-block rounded sm:px-4 sm:py-1"
            >
              {previouslySaved ? 'Update' : 'Add'}
            </Button>
          )}
        </div>
      </div>
    </>
  );
};

interface BiomarkerPanelValuesProps {
	data: PanelDto;
	isInAutosave: boolean;
	onDelete: () => void;
	onRemove: () => void;
	onSetActive: () => void;
}

function getDiseaseLocationLabel(
  cancerLocationId: string | undefined,
  config: CancerConfig[] | undefined
) {
  if (!cancerLocationId || !config) return '';

  const cancerType = config.find((x) => x.locations.some((y) => y.value === cancerLocationId));

  return cancerType
    ? `${cancerType.label} - ${
      cancerType.locations.find((x) => x.value === cancerLocationId)?.label
		  }`
    : '';
}

function getDiseaseTypeLabel(
  cancerLocationId: string | undefined,
  cancerPathologyId: string | undefined,
  config: CancerConfig[] | undefined
) {
  if (!cancerLocationId || !cancerPathologyId || !config) return '';

  const cancerType = config.find((x) => x.locations.some((y) => y.value === cancerLocationId));

  return cancerType
    ? cancerType.pathologies.find((pathology) => pathology.value === cancerPathologyId)?.label
    : '';
}

const BiomarkerPanelValues = ({
  data,
  isInAutosave,
  onDelete,
  onRemove,
  onSetActive
}: BiomarkerPanelValuesProps) => {
  const configs = usePostCaseConfigs(['cancer']);

  const { cancerLocationId, cancerPathologyId } = data;

  const [diseaseLocationLabel, diseaseTypeLabel] = React.useMemo(() => {
    return [
      getDiseaseLocationLabel(cancerLocationId, configs?.cancer),
      getDiseaseTypeLabel(cancerLocationId, cancerPathologyId, configs?.cancer)
    ];
  }, [configs?.cancer, cancerLocationId, cancerPathologyId]);

  return (
    <>
      <div className="flex flex-wrap sm:flex-nowrap">
        <div className="w-1/2 mb-2 sm:mb-0 sm:pr-4 sm:w-auto">
          <label htmlFor="panelName" className="text-sm font-medium text-gray-800">
						Panel Name
          </label>
          <p className="text-sm">{data.panelName || '—'}</p>
        </div>
        <div className="w-1/2 mb-2 sm:mb-0 sm:px-4 sm:w-auto">
          <label htmlFor="reportDate" className="text-sm font-semibold text-gray-800">
						Report Date
          </label>
          <p className="text-sm">
            {(data.reportDate && data.reportDate.toLocaleDateString()) || '—'}
          </p>
        </div>
        <div className="w-1/2 mb-2 sm:mb-0 sm:px-4 sm:w-auto">
          <label htmlFor="panelType" className="text-sm font-semibold text-gray-800">
						Panel Type
          </label>
          <p className="text-sm">{data.panelType || '—'}</p>
        </div>
        <div className="w-1/2 mb-2 sm:mb-0 sm:px-4 sm:w-auto">
          <label htmlFor="reportingLab" className="text-sm font-semibold text-gray-800">
						Reporting Lab
          </label>
          <p className="text-sm">{data.reportingLab || '—'}</p>
        </div>
        <div className="w-1/2 sm:px-4 sm:w-auto">
          <label htmlFor="collectionDate" className="text-sm font-semibold text-gray-800">
						Collected Date
          </label>
          <p className="text-sm">
            {(data.collectionDate && data.collectionDate.toLocaleDateString()) || '—'}
          </p>
        </div>
        <div className="w-1/2 sm:px-4 sm:w-auto">
          <label htmlFor="collectionSite" className="text-sm font-semibold text-gray-800">
						Collection Site
          </label>
          <p className="text-sm">{data.collectionSite || '—'}</p>
        </div>
        <div className="w-1/2 sm:px-4 sm:w-auto">
          <label htmlFor="diseaseLocationLabel" className="text-sm font-semibold text-gray-800">
						Disease Location
          </label>
          <p className="text-sm">{diseaseLocationLabel || '—'}</p>
        </div>
        <div className="w-1/2 sm:px-4 sm:w-auto">
          <label htmlFor="diseaseTypeLabel" className="text-sm font-semibold text-gray-800">
						Disease Type
          </label>
          <p className="text-sm">{diseaseTypeLabel || '—'}</p>
        </div>
        <div className="flex items-center ml-auto -mt-4 space-x-2">
          <FaPencilAlt
            data-cy="biomarker-values-edit-button"
            className="box-content p-2 mr-px text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
            onClick={onSetActive}
          />
          {isInAutosave ? (
            <Modal
              triggerElement={
                <FaTrash className="box-content p-2 text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800" />
              }
            >
              <Modal.Header title="Delete Panel" showClose={true} />
              <Modal.Content>
                <p>Are you sure you want to delete this panel?</p>
              </Modal.Content>
              <Modal.Buttons
                buttons={[
                  {
                    label: 'Cancel',
                    style: 'secondary',
                    color: 'gray',
                    closeAfterClick: true,
                    className: 'mr-2'
                  },
                  {
                    label: 'Delete',
                    style: 'primary',
                    color: 'red',
                    closeAfterClick: true,
                    onClick: () => {
                      setTimeout(onDelete, 300);
                    }
                  }
                ]}
              />
              {/* TODO: Remove set timeout. right now, the modal needs to live outside the removed part of the component but also still have a trigger in the right place
                      which means that clicking 'Delete' removes the whole modal tree before the close animation can finish. */}
            </Modal>
          ) : (
            <FaTrash
              className="box-content p-2 text-gray-600 transition-colors duration-200 cursor-pointer hover:text-gray-800"
              onClick={onRemove}
            />
          )}
        </div>
      </div>
    </>
  );
};

export { BiomarkerPanel as default };
